import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Target,
  AlertTriangle,
  Clock,
  Users,
  TrendingUp,
  Phone,
  Mail,
  MapPin,
  Building,
  MessageSquare,
  MessageCircle,
  ChevronRight,
  ChevronLeft,
  MoreVertical,
  Plus,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  PauseCircle,
  ArrowRight,
  ArrowLeft,
  Calendar,
  Euro,
  Filter,
  Eye,
  Bell,
  Send,
  FileText,
  FolderOpen,
  Banknote,
  CheckSquare,
  Square,
  ExternalLink,
  Truck,
  User,
  FileCheck,
  Wrench,
  Tractor,
  Tag,
  Wallet,
  BarChart2,
  Edit3,
  Save,
  X,
  Trash2,
  Settings,
  Star,
  Activity,
} from 'lucide-react';
import api from '@/lib/api';

const formatAmount = (amount) => {
  if (!amount) return '0 €';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const getStatusColor = (status) => {
  const colors = {
    'brouillon': 'bg-slate-100 text-slate-700',
    'envoye': 'bg-blue-100 text-blue-700',
    'relance_1': 'bg-amber-100 text-amber-700',
    'relance_2': 'bg-orange-100 text-orange-700',
    'relance_3': 'bg-red-100 text-red-700',
    'en_etude_financement': 'bg-purple-100 text-purple-700',
    'accepte': 'bg-green-100 text-green-700',
    'commande': 'bg-emerald-100 text-emerald-700',
    'livre': 'bg-teal-100 text-teal-700',
    'archive': 'bg-gray-100 text-gray-500',
    'sans_suite': 'bg-gray-100 text-gray-500',
  };
  return colors[status] || 'bg-slate-100 text-slate-700';
};

const getStatusLabel = (status) => {
  const labels = {
    'brouillon': 'Brouillon',
    'envoye': 'Envoyé',
    'relance_1': 'Relance 1',
    'relance_2': 'Relance 2',
    'relance_3': 'Relance 3',
    'en_etude_financement': 'En étude financement',
    'accepte': 'Accepté',
    'commande': 'Commandé',
    'livre': 'Livré',
    'archive': 'Archivé',
    'sans_suite': 'Sans suite',
  };
  return labels[status] || status;
};

// ============= OPPORTUNITY DETAIL PAGE =============
const OpportunityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [opportunity, setOpportunity] = useState(null);
  const [activeTab, setActiveTab] = useState('devis');
  const [markingDocument, setMarkingDocument] = useState(null);
  
  // Prospect fiche states
  const [prospectEdit, setProspectEdit] = useState(null); // 'client'|'besoin'|'machines'|'maturity'
  const [prospectForm, setProspectForm] = useState({});
  const [savingProspect, setSavingProspect] = useState(false);
  const [newMachine, setNewMachine] = useState(null); // machine being added
  const [analyzingNote, setAnalyzingNote] = useState(false);
  const [suggestedTask, setSuggestedTask] = useState(null);
  const [creatingTask, setCreatingTask] = useState(false);
  
  // Dialog states
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [activityForm, setActivityForm] = useState({
    contact_type: '',
    contact_result: '',
    notes: '',
    next_action_date: '',
    new_stage: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchOpportunity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/opportunities/${id}`);
      setOpportunity(response.data);
    } catch (error) {
      console.error('Error fetching opportunity:', error);
      toast.error('Erreur lors du chargement de l\'opportunité');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOpportunity();
  }, [fetchOpportunity]);

  // ---- Prospect handlers ----
  const startEditProspect = (card) => {
    setProspectForm(opportunity?.prospect || {});
    setProspectEdit(card);
  };

  const cancelEditProspect = () => {
    setProspectEdit(null);
    setNewMachine(null);
  };

  const saveProspect = async (patch) => {
    try {
      setSavingProspect(true);
      await api.patch(`/opportunities/${id}/prospect`, patch);
      toast.success('Fiche prospect mise à jour');
      setProspectEdit(null);
      setNewMachine(null);
      fetchOpportunity();
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingProspect(false);
    }
  };

  // Analyse de note prospect pour tâches automatiques
  const analyzeProspectNote = async (noteContent) => {
    if (!noteContent || noteContent.length < 5) {
      setSuggestedTask(null);
      return;
    }
    
    try {
      setAnalyzingNote(true);
      const response = await api.post(`/opportunities/${id}/analyze-note`, {
        note_content: noteContent,
        client_name: opportunity?.client?.name
      });
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setSuggestedTask(response.data.suggestions[0]);
      } else {
        setSuggestedTask(null);
      }
    } catch (error) {
      console.error('Error analyzing note:', error);
      setSuggestedTask(null);
    } finally {
      setAnalyzingNote(false);
    }
  };

  const createTaskFromNote = async (noteContent) => {
    if (!noteContent) return;
    
    try {
      setCreatingTask(true);
      const response = await api.post(`/opportunities/${id}/create-task-from-note`, {
        note_content: noteContent,
        client_name: opportunity?.client?.name
      });
      
      if (response.data.task_created) {
        toast.success(`Tâche créée: ${response.data.task.title}`);
        setSuggestedTask(null);
      } else {
        toast.info('Aucune tâche suggérée pour cette note');
      }
    } catch (error) {
      toast.error('Erreur lors de la création de la tâche');
    } finally {
      setCreatingTask(false);
    }
  };

  const deleteMachine = async (machineId) => {
    const machines = (opportunity?.prospect?.existing_machines || []).filter(m => m.id !== machineId);
    await saveProspect({ existing_machines: machines });
  };

  const addOrUpdateMachine = async () => {
    if (!newMachine?.marque && !newMachine?.modele) return;
    const existing = opportunity?.prospect?.existing_machines || [];
    const idx = existing.findIndex(m => m.id === newMachine.id);
    let updated;
    if (idx >= 0) {
      updated = existing.map((m, i) => i === idx ? newMachine : m);
    } else {
      updated = [...existing, { ...newMachine, id: Date.now().toString() }];
    }
    await saveProspect({ existing_machines: updated });
  };
  // ---- End Prospect handlers ----

  const handleStageChange = async (newStage) => {
    try {
      setSaving(true);
      await api.patch(`/opportunities/${id}/stage`, { new_stage: newStage });
      toast.success('Étape mise à jour');
      fetchOpportunity();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkDocument = async (financingId, docId, received) => {
    try {
      setMarkingDocument(docId);
      // Use toggle-checked endpoint instead of mark-received
      await api.post(`/financing/requests/${financingId}/toggle-checked/${docId}`);
      toast.success(received ? 'Document marqué comme reçu' : 'Document marqué comme manquant');
      fetchOpportunity();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du document');
    } finally {
      setMarkingDocument(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!opportunity) {
    return (
      <div className="p-6 text-center">
        <p className="text-slate-500">Opportunité non trouvée</p>
        <Button onClick={() => navigate('/admin/opportunities')} className="mt-4">
          Retour aux opportunités
        </Button>
      </div>
    );
  }

  const client = opportunity.client || {};
  const financing = opportunity.financing;
  const hasFinancing = financing && financing.docs_total > 0;

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/admin/opportunities')}
        className="mb-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour aux opportunités
      </Button>

      {/* Header Card with Client & Financing Info */}
      <Card className="border-l-4" style={{ borderLeftColor: opportunity.stage_config?.color || '#f97316' }}>
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            {/* Client Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">
                    {client.name || opportunity.title || 'Client'}
                  </h1>
                  <p className="text-slate-500">{client.company || ''}</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                {client.phone && (
                  <a href={`tel:${client.phone}`} className="flex items-center gap-1 hover:text-orange-600">
                    <Phone className="w-4 h-4" /> {client.phone}
                  </a>
                )}
                {client.email && (
                  <a href={`mailto:${client.email}`} className="flex items-center gap-1 hover:text-orange-600">
                    <Mail className="w-4 h-4" /> {client.email}
                  </a>
                )}
              </div>

              {/* Pipeline Stage Selector */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-slate-500">Étape :</span>
                {opportunity.all_stages?.map((stage) => (
                  <Button
                    key={stage.id}
                    size="sm"
                    variant={opportunity.stage === stage.id ? 'default' : 'outline'}
                    className={`text-xs ${opportunity.stage === stage.id ? '' : 'opacity-60'}`}
                    style={{
                      backgroundColor: opportunity.stage === stage.id ? stage.color : undefined,
                      borderColor: stage.color,
                      color: opportunity.stage === stage.id ? 'white' : stage.color,
                    }}
                    onClick={() => handleStageChange(stage.id)}
                    disabled={saving}
                  >
                    {stage.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* Stats & Financing Banner */}
            <div className="lg:text-right space-y-3">
              <div className="flex items-center gap-4 justify-end">
                <div className="text-right">
                  <p className="text-sm text-slate-500">Devis</p>
                  <p className="text-2xl font-bold text-slate-900">{opportunity.devis_count || 0}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Montant total</p>
                  <p className="text-2xl font-bold text-green-600">{formatAmount(opportunity.total_amount)}</p>
                </div>
              </div>

              {/* Financing Banner */}
              {hasFinancing && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Banknote className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-800">Dossier Financement</span>
                    </div>
                    <Badge variant="outline" className="bg-purple-100 text-purple-700 border-purple-300">
                      {financing.docs_received}/{financing.docs_total} pièces
                    </Badge>
                  </div>
                  <Progress 
                    value={financing.progress_percent} 
                    className="h-2 bg-purple-100"
                  />
                  <p className="text-xs text-purple-600 mt-1">
                    Devis {financing.devis_reference} - {financing.progress_percent}% complété
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Devis | Financement | Activités */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
          <TabsTrigger value="devis" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Devis ({opportunity.devis_count || 0})
          </TabsTrigger>
          <TabsTrigger value="financement" className="flex items-center gap-2">
            <Banknote className="w-4 h-4" />
            Financement
            {hasFinancing && (
              <Badge variant="secondary" className="ml-1 text-xs">
                {financing.docs_received}/{financing.docs_total}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="activites" className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Activités
          </TabsTrigger>
          <TabsTrigger value="fiche_prospect" className="flex items-center gap-2" data-testid="tab-fiche-prospect">
            <User className="w-4 h-4" />
            Fiche Prospect
          </TabsTrigger>
        </TabsList>

        {/* Devis Tab */}
        <TabsContent value="devis" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Devis liés à cette opportunité</CardTitle>
              <Link to={`/admin/devis/new?client_id=${opportunity.client_id}`}>
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Nouveau devis
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {opportunity.devis?.length > 0 ? (
                <div className="space-y-3">
                  {opportunity.devis.map((devis) => (
                    <div 
                      key={devis.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-slate-900">{devis.reference}</span>
                            <Badge className={getStatusColor(devis.status)}>
                              {getStatusLabel(devis.status)}
                            </Badge>
                            {devis.financing_request_id && (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <Banknote className="w-3 h-3 mr-1" />
                                En financement
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500">
                            {devis.mini_pelle_name || 'Machine non définie'} • {formatDate(devis.created_at)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-semibold text-green-600">{formatAmount(devis.total)}</span>
                        <Link to={`/admin/devis/${devis.id}`}>
                          <Button size="sm" variant="outline">
                            <Eye className="w-4 h-4 mr-2" />
                            Voir
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun devis pour cette opportunité</p>
                  <Link to={`/admin/devis/new?client_id=${opportunity.client_id}`}>
                    <Button size="sm" className="mt-3 bg-orange-500 hover:bg-orange-600">
                      <Plus className="w-4 h-4 mr-2" />
                      Créer un devis
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Financement Tab */}
        <TabsContent value="financement" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Banknote className="w-5 h-5 text-purple-600" />
                Dossiers de Financement
              </CardTitle>
            </CardHeader>
            <CardContent>
              {opportunity.financing_requests?.length > 0 ? (
                <div className="space-y-6">
                  {opportunity.financing_requests.map((fin) => (
                    <div key={fin.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="font-semibold text-slate-900">
                            Dossier pour devis {fin.devis_reference}
                          </h4>
                          <p className="text-sm text-slate-500">
                            {fin.docs_received}/{fin.docs_total} documents reçus
                          </p>
                        </div>
                        <div className="text-right">
                          <Progress 
                            value={fin.progress_percent} 
                            className="w-32 h-2"
                          />
                          <span className="text-sm text-slate-500">{fin.progress_percent}%</span>
                        </div>
                      </div>
                      
                      {/* Documents Checklist */}
                      <div className="grid gap-2">
                        {fin.documents?.map((doc) => (
                          <div 
                            key={doc.doc_id || doc.id}
                            className={`flex items-center justify-between p-3 rounded-lg border ${
                              doc.status === 'validated' || doc.status === 'uploaded' || doc.manually_checked
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-slate-50 border-slate-200'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <button
                                onClick={() => handleMarkDocument(fin.id, doc.doc_id || doc.id, doc.status === 'pending')}
                                disabled={markingDocument === (doc.doc_id || doc.id)}
                                className="focus:outline-none"
                              >
                                {markingDocument === (doc.doc_id || doc.id) ? (
                                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                                ) : (doc.status === 'validated' || doc.status === 'uploaded' || doc.manually_checked) ? (
                                  <CheckSquare className="w-5 h-5 text-green-600 cursor-pointer hover:text-green-700" />
                                ) : (
                                  <Square className="w-5 h-5 text-slate-400 cursor-pointer hover:text-slate-600" />
                                )}
                              </button>
                              <span className={(doc.status === 'validated' || doc.status === 'uploaded' || doc.manually_checked) ? 'text-green-800' : 'text-slate-700'}>
                                {doc.name}
                              </span>
                            </div>
                            {doc.required && doc.status === 'pending' && !doc.manually_checked && (
                              <Badge variant="outline" className="text-red-600 border-red-200">
                                Requis
                              </Badge>
                            )}
                            {(doc.status === 'validated' || doc.status === 'uploaded' || doc.manually_checked) && doc.validated_at && (
                              <span className="text-xs text-green-600">
                                Reçu le {formatDate(doc.validated_at)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Link to devis */}
                      <div className="mt-4 flex gap-2">
                        <Link to={`/admin/devis/${fin.devis_id}`}>
                          <Button size="sm" variant="outline">
                            <FileText className="w-4 h-4 mr-2" />
                            Voir le devis
                          </Button>
                        </Link>
                        <Link to={`/admin/financement/${fin.id}`}>
                          <Button size="sm" variant="outline" className="text-purple-600 border-purple-300">
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Détail financement
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Banknote className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucun dossier de financement</p>
                  <p className="text-sm mt-1">
                    Passez un devis en statut "En étude financement" pour créer un dossier
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activités Tab */}
        <TabsContent value="activites" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Historique des activités</CardTitle>
              <Button 
                size="sm" 
                onClick={() => setActivityDialogOpen(true)}
                className="bg-orange-500 hover:bg-orange-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une activité
              </Button>
            </CardHeader>
            <CardContent>
              {opportunity.activities?.length > 0 ? (
                <div className="space-y-4">
                  {opportunity.activities.slice().reverse().map((activity, idx) => (
                    <div 
                      key={activity.id || idx}
                      className="flex gap-4 pb-4 border-b last:border-b-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <MessageSquare className="w-4 h-4 text-slate-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="font-medium">{activity.type || 'Note'}</span>
                          <span className="text-slate-400">•</span>
                          <span className="text-slate-500">{formatDate(activity.created_at)}</span>
                        </div>
                        <p className="text-slate-700 mt-1">{activity.notes || activity.content}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune activité enregistrée</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

      {/* ===== FICHE PROSPECT TAB CONTENT ===== */}
      <TabsContent value="fiche_prospect" className="mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* ===== CARD 1 — INFORMATIONS CLIENT (Bleu) ===== */}
          <div className="rounded-2xl border-0 shadow-md overflow-hidden" data-testid="prospect-card-client">
            <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Informations Client</h3>
                  <p className="text-blue-100 text-xs">Identification & secteur</p>
                </div>
              </div>
              {prospectEdit !== 'client' ? (
                <button onClick={() => startEditProspect('client')} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" data-testid="edit-prospect-client">
                  <Edit3 className="w-4 h-4 text-white" />
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={cancelEditProspect} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors">
                    <X className="w-4 h-4 text-white" />
                  </button>
                  <button
                    disabled={savingProspect}
                    onClick={() => saveProspect({
                      sector: prospectForm.sector,
                      client_type: prospectForm.client_type,
                      siret: prospectForm.siret,
                      notes_qualification: prospectForm.notes_qualification,
                    })}
                    className="p-2 rounded-lg bg-white text-blue-600 hover:bg-blue-50 transition-colors"
                    data-testid="save-prospect-client"
                  >
                    {savingProspect ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
            <div className="bg-white p-5 space-y-4">
              {/* Client name + contact (read from opportunity.client) */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="text-xs text-blue-500 font-medium mb-1">Nom</p>
                  <p className="font-semibold text-slate-900 text-sm">{opportunity?.client?.name || opportunity?.client_name || '—'}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3">
                  <p className="text-xs text-blue-500 font-medium mb-1">Téléphone</p>
                  <p className="font-semibold text-slate-900 text-sm">{opportunity?.client?.phone || '—'}</p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 col-span-2">
                  <p className="text-xs text-blue-500 font-medium mb-1">Email</p>
                  <p className="font-semibold text-slate-900 text-sm">{opportunity?.client?.email || '—'}</p>
                </div>
              </div>
              {/* Editable fields */}
              {prospectEdit === 'client' ? (
                <div className="space-y-3 pt-2 border-t">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Secteur d'activité</label>
                      <select
                        value={prospectForm.sector || ''}
                        onChange={e => setProspectForm({ ...prospectForm, sector: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="">Sélectionner...</option>
                        {['BTP', 'Agriculture', 'Paysagisme', 'Collectivité', 'Particulier', 'Transport', 'Industrie', 'Mines & Carrières', 'Démolition'].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Type de client</label>
                      <select
                        value={prospectForm.client_type || ''}
                        onChange={e => setProspectForm({ ...prospectForm, client_type: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="">Sélectionner...</option>
                        {['Professionnel', 'Particulier', 'Collectivité', 'Revendeur'].map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">SIRET</label>
                    <input
                      value={prospectForm.siret || ''}
                      onChange={e => setProspectForm({ ...prospectForm, siret: e.target.value })}
                      placeholder="123 456 789 00012"
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Notes de qualification</label>
                    <textarea
                      value={prospectForm.notes_qualification || ''}
                      onChange={e => setProspectForm({ ...prospectForm, notes_qualification: e.target.value })}
                      placeholder="Contexte, source du lead, remarques..."
                      rows={3}
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Secteur</p>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${opportunity?.prospect?.sector ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-400'}`}>
                      <Tag className="w-3 h-3" />
                      {opportunity?.prospect?.sector || 'Non renseigné'}
                    </span>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Type</p>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${opportunity?.prospect?.client_type ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-400'}`}>
                      <Users className="w-3 h-3" />
                      {opportunity?.prospect?.client_type || 'Non renseigné'}
                    </span>
                  </div>
                  {opportunity?.prospect?.siret && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 mb-1">SIRET</p>
                      <p className="text-sm font-mono text-slate-700">{opportunity.prospect.siret}</p>
                    </div>
                  )}
                  {opportunity?.prospect?.notes_qualification && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500 mb-1">Notes de qualification</p>
                      <p className="text-sm text-slate-700 bg-blue-50 rounded-lg p-2">{opportunity.prospect.notes_qualification}</p>
                    </div>
                  )}
                  {!opportunity?.prospect?.sector && !opportunity?.prospect?.notes_qualification && (
                    <div className="col-span-2 text-center py-3 text-slate-400 text-sm">
                      Cliquez sur <Edit3 className="w-3.5 h-3.5 inline mx-1" /> pour remplir la fiche
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ===== CARD 2 — BESOIN EXACT (Ambre) ===== */}
          <div className="rounded-2xl border-0 shadow-md overflow-hidden" data-testid="prospect-card-besoin">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Besoin Exact</h3>
                  <p className="text-amber-100 text-xs">Modèle, options & budget</p>
                </div>
              </div>
              {prospectEdit !== 'besoin' ? (
                <button onClick={() => startEditProspect('besoin')} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors" data-testid="edit-prospect-besoin">
                  <Edit3 className="w-4 h-4 text-white" />
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={cancelEditProspect} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"><X className="w-4 h-4 text-white" /></button>
                  <button
                    disabled={savingProspect}
                    onClick={() => saveProspect({
                      model_souhaite: prospectForm.model_souhaite,
                      options_souhaitees: prospectForm.options_souhaitees || [],
                      budget_min: prospectForm.budget_min ? Number(prospectForm.budget_min) : null,
                      budget_max: prospectForm.budget_max ? Number(prospectForm.budget_max) : null,
                      usage_prevu: prospectForm.usage_prevu,
                      delai_achat: prospectForm.delai_achat,
                      financement_souhaite: prospectForm.financement_souhaite,
                      demo_souhaitee: prospectForm.demo_souhaitee,
                      notes_besoin: prospectForm.notes_besoin,
                    })}
                    className="p-2 rounded-lg bg-white text-amber-600 hover:bg-amber-50 transition-colors"
                    data-testid="save-prospect-besoin"
                  >
                    {savingProspect ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
            <div className="bg-white p-5 space-y-4">
              {prospectEdit === 'besoin' ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Modèle souhaité</label>
                    <input
                      value={prospectForm.model_souhaite || ''}
                      onChange={e => setProspectForm({ ...prospectForm, model_souhaite: e.target.value })}
                      placeholder="Ex : LiuGong 308E-X, MECALAC 6MCR..."
                      className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-2">Options souhaitées</label>
                    <div className="grid grid-cols-2 gap-2">
                      {['Godet HD', 'Godet curage', 'Brise-roche', 'Attache rapide', 'Chenilles caoutchouc', 'Trémie de tri', 'Climatisation', 'Prise hydraulique additionnelle', 'Cabine fermée', 'Châssis élargissable'].map(opt => (
                        <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(prospectForm.options_souhaitees || []).includes(opt)}
                            onChange={e => {
                              const opts = prospectForm.options_souhaitees || [];
                              setProspectForm({
                                ...prospectForm,
                                options_souhaitees: e.target.checked ? [...opts, opt] : opts.filter(o => o !== opt)
                              });
                            }}
                            className="accent-amber-500"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Budget min (€)</label>
                      <input type="number" value={prospectForm.budget_min || ''} onChange={e => setProspectForm({ ...prospectForm, budget_min: e.target.value })} placeholder="30000" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Budget max (€)</label>
                      <input type="number" value={prospectForm.budget_max || ''} onChange={e => setProspectForm({ ...prospectForm, budget_max: e.target.value })} placeholder="50000" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Usage prévu</label>
                    <textarea value={prospectForm.usage_prevu || ''} onChange={e => setProspectForm({ ...prospectForm, usage_prevu: e.target.value })} placeholder="Terrassement, démolition, paysagisme..." rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Délai d'achat</label>
                    <select value={prospectForm.delai_achat || ''} onChange={e => setProspectForm({ ...prospectForm, delai_achat: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400">
                      <option value="">Sélectionner...</option>
                      {['Immédiat', '1 à 3 mois', '3 à 6 mois', '6 à 12 mois', 'Plus de 12 mois'].map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={!!prospectForm.financement_souhaite} onChange={e => setProspectForm({ ...prospectForm, financement_souhaite: e.target.checked })} className="accent-amber-500" />
                      Financement souhaité
                    </label>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={!!prospectForm.demo_souhaitee} onChange={e => setProspectForm({ ...prospectForm, demo_souhaitee: e.target.checked })} className="accent-amber-500" />
                      Démo souhaitée
                    </label>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Notes sur le besoin</label>
                    <textarea value={prospectForm.notes_besoin || ''} onChange={e => setProspectForm({ ...prospectForm, notes_besoin: e.target.value })} placeholder="Remarques spécifiques, contraintes techniques..." rows={2} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none" />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Model */}
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-xl">
                    <Tractor className="w-5 h-5 text-amber-600 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-amber-600 font-medium">Modèle souhaité</p>
                      <p className="font-semibold text-slate-900 text-sm">{opportunity?.prospect?.model_souhaite || 'Non renseigné'}</p>
                    </div>
                  </div>
                  {/* Options */}
                  {(opportunity?.prospect?.options_souhaitees || []).length > 0 && (
                    <div>
                      <p className="text-xs text-slate-500 mb-2">Options souhaitées</p>
                      <div className="flex flex-wrap gap-1.5">
                        {opportunity.prospect.options_souhaitees.map(opt => (
                          <span key={opt} className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">{opt}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Budget */}
                  {(opportunity?.prospect?.budget_min || opportunity?.prospect?.budget_max) && (
                    <div className="flex items-center gap-3 p-3 bg-green-50 rounded-xl">
                      <Wallet className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-green-600 font-medium">Budget</p>
                        <p className="font-semibold text-slate-900 text-sm">
                          {opportunity.prospect.budget_min ? `${Number(opportunity.prospect.budget_min).toLocaleString('fr-FR')} €` : ''}
                          {opportunity.prospect.budget_min && opportunity.prospect.budget_max ? ' — ' : ''}
                          {opportunity.prospect.budget_max ? `${Number(opportunity.prospect.budget_max).toLocaleString('fr-FR')} €` : ''}
                        </p>
                      </div>
                    </div>
                  )}
                  {/* Timeline + flags */}
                  <div className="grid grid-cols-3 gap-2">
                    {opportunity?.prospect?.delai_achat && (
                      <div className="text-center p-2 bg-slate-50 rounded-xl">
                        <Clock className="w-4 h-4 text-slate-500 mx-auto mb-1" />
                        <p className="text-xs text-slate-500">Délai</p>
                        <p className="text-xs font-semibold text-slate-800">{opportunity.prospect.delai_achat}</p>
                      </div>
                    )}
                    <div className={`text-center p-2 rounded-xl ${opportunity?.prospect?.financement_souhaite ? 'bg-purple-50' : 'bg-slate-50'}`}>
                      <Banknote className={`w-4 h-4 mx-auto mb-1 ${opportunity?.prospect?.financement_souhaite ? 'text-purple-500' : 'text-slate-300'}`} />
                      <p className="text-xs text-slate-500">Financement</p>
                      <p className="text-xs font-semibold">{opportunity?.prospect?.financement_souhaite ? 'Souhaité' : 'Non'}</p>
                    </div>
                    <div className={`text-center p-2 rounded-xl ${opportunity?.prospect?.demo_souhaitee ? 'bg-blue-50' : 'bg-slate-50'}`}>
                      <Eye className={`w-4 h-4 mx-auto mb-1 ${opportunity?.prospect?.demo_souhaitee ? 'text-blue-500' : 'text-slate-300'}`} />
                      <p className="text-xs text-slate-500">Démo</p>
                      <p className="text-xs font-semibold">{opportunity?.prospect?.demo_souhaitee ? 'Souhaitée' : 'Non'}</p>
                    </div>
                  </div>
                  {opportunity?.prospect?.usage_prevu && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Usage prévu</p>
                      <p className="text-sm text-slate-700 bg-orange-50 rounded-lg p-2">{opportunity.prospect.usage_prevu}</p>
                    </div>
                  )}
                  {!opportunity?.prospect?.model_souhaite && !(opportunity?.prospect?.options_souhaitees?.length) && (
                    <div className="text-center py-4 text-slate-400 text-sm">
                      Cliquez sur <Edit3 className="w-3.5 h-3.5 inline mx-1" /> pour renseigner le besoin
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ===== CARD 3 — MACHINES EXISTANTES (Vert) ===== */}
          <div className="rounded-2xl border-0 shadow-md overflow-hidden" data-testid="prospect-card-machines">
            <div className="bg-gradient-to-r from-emerald-600 to-teal-500 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <Wrench className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Machines Existantes</h3>
                  <p className="text-emerald-100 text-xs">{(opportunity?.prospect?.existing_machines || []).length} machine(s) recensée(s)</p>
                </div>
              </div>
              <button
                onClick={() => { setNewMachine({ marque: '', modele: '', annee: '', etat: '', notes: '' }); setProspectEdit('machines'); }}
                className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
                data-testid="add-machine-btn"
              >
                <Plus className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="bg-white p-5">
              {/* Add machine form */}
              {prospectEdit === 'machines' && newMachine && (
                <div className="mb-4 p-4 bg-emerald-50 rounded-xl border border-emerald-200 space-y-3">
                  <p className="text-sm font-semibold text-emerald-700">{newMachine.id ? 'Modifier la machine' : 'Ajouter une machine'}</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Marque</label>
                      <input value={newMachine.marque} onChange={e => setNewMachine({ ...newMachine, marque: e.target.value })} placeholder="Kubota, Takeuchi..." className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Modèle</label>
                      <input value={newMachine.modele} onChange={e => setNewMachine({ ...newMachine, modele: e.target.value })} placeholder="KX016-4" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">Année</label>
                      <input type="number" value={newMachine.annee} onChange={e => setNewMachine({ ...newMachine, annee: e.target.value })} placeholder="2018" className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 block mb-1">État</label>
                      <select value={newMachine.etat} onChange={e => setNewMachine({ ...newMachine, etat: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400">
                        <option value="">Sélectionner...</option>
                        {['Bon état', 'Usée', 'À remplacer', 'Louée', 'Vendue'].map(e => <option key={e} value={e}>{e}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 block mb-1">Notes</label>
                    <input value={newMachine.notes} onChange={e => setNewMachine({ ...newMachine, notes: e.target.value })} placeholder="Heures, historique..." className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400" />
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button onClick={cancelEditProspect} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50">Annuler</button>
                    <button
                      onClick={addOrUpdateMachine}
                      disabled={savingProspect}
                      className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                      data-testid="confirm-add-machine"
                    >
                      {savingProspect ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Enregistrer
                    </button>
                  </div>
                </div>
              )}
              {/* Machine list */}
              {(opportunity?.prospect?.existing_machines || []).length === 0 && prospectEdit !== 'machines' ? (
                <div className="text-center py-6 text-slate-400">
                  <Tractor className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Aucune machine recensée</p>
                  <p className="text-xs mt-1">Cliquez sur <Plus className="w-3.5 h-3.5 inline mx-1" /> pour en ajouter</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(opportunity?.prospect?.existing_machines || []).map(machine => {
                    const etatColors = {
                      'Bon état': 'bg-green-100 text-green-700',
                      'Usée': 'bg-yellow-100 text-yellow-700',
                      'À remplacer': 'bg-red-100 text-red-700',
                      'Louée': 'bg-blue-100 text-blue-700',
                      'Vendue': 'bg-gray-100 text-gray-600',
                    };
                    return (
                      <div key={machine.id} className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <Tractor className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 text-sm">{machine.marque} {machine.modele}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {machine.annee && <span className="text-xs text-slate-500">{machine.annee}</span>}
                              {machine.etat && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${etatColors[machine.etat] || 'bg-slate-100 text-slate-600'}`}>{machine.etat}</span>
                              )}
                            </div>
                            {machine.notes && <p className="text-xs text-slate-500 mt-0.5">{machine.notes}</p>}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => { setNewMachine({ ...machine }); setProspectEdit('machines'); }}
                            className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-100 rounded-lg transition-colors"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => deleteMachine(machine.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ===== CARD 4 — MATURITÉ DU PROJET (Violet) ===== */}
          <div className="rounded-2xl border-0 shadow-md overflow-hidden" data-testid="prospect-card-maturite">
            <div className="bg-gradient-to-r from-violet-600 to-purple-600 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <BarChart2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-sm">Maturité du Projet</h3>
                  <p className="text-purple-200 text-xs">Pipeline, devis & financement</p>
                </div>
              </div>
              <span className="px-2.5 py-1 bg-white/20 text-white text-xs font-bold rounded-full">
                {opportunity?.progress || 0}%
              </span>
            </div>
            <div className="bg-white p-5 space-y-4">
              {/* Pipeline progress */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-medium text-slate-600">Avancement pipeline</span>
                  <span className="text-xs font-bold text-violet-700">{opportunity?.stage_config?.label || opportunity?.stage}</span>
                </div>
                {/* Visual stages */}
                <div className="flex gap-0.5">
                  {(opportunity?.all_stages || []).map((stage, idx) => {
                    const currentIdx = (opportunity?.all_stages || []).findIndex(s => s.id === opportunity?.stage);
                    const isPast = idx < currentIdx;
                    const isCurrent = idx === currentIdx;
                    return (
                      <div
                        key={stage.id}
                        className={`h-2 flex-1 rounded-full transition-all ${
                          isCurrent ? 'bg-violet-600' : isPast ? 'bg-violet-300' : 'bg-slate-100'
                        }`}
                        title={stage.label}
                      />
                    );
                  })}
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-slate-400">Nouveau lead</span>
                  <span className="text-xs text-slate-400">Clôture</span>
                </div>
              </div>

              {/* Devis summary */}
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <p className="text-xl font-bold text-blue-700">{opportunity?.devis_count || 0}</p>
                  <p className="text-xs text-blue-600 mt-0.5">Devis</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-xl">
                  <p className="text-sm font-bold text-green-700">
                    {opportunity?.total_amount ? `${Number(opportunity.total_amount).toLocaleString('fr-FR')} €` : '0 €'}
                  </p>
                  <p className="text-xs text-green-600 mt-0.5">Montant total</p>
                </div>
                <div className={`text-center p-3 rounded-xl ${opportunity?.financing ? 'bg-purple-50' : 'bg-slate-50'}`}>
                  <Banknote className={`w-5 h-5 mx-auto mb-1 ${opportunity?.financing ? 'text-purple-600' : 'text-slate-300'}`} />
                  <p className="text-xs text-slate-600">{opportunity?.financing ? `${opportunity.financing.progress_percent || 0}%` : 'Aucun'}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Financement</p>
                </div>
              </div>

              {/* Last activity */}
              {opportunity?.activities?.length > 0 && (() => {
                const lastAct = [...(opportunity.activities)].reverse()[0];
                return (
                  <div className="flex items-start gap-3 p-3 bg-violet-50 rounded-xl">
                    <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Activity className="w-3.5 h-3.5 text-violet-600" />
                    </div>
                    <div>
                      <p className="text-xs text-violet-600 font-medium">Dernière activité</p>
                      <p className="text-sm text-slate-700 font-medium">{lastAct.type || lastAct.contact_type || 'Note'}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{lastAct.notes || lastAct.content || ''}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{formatDate(lastAct.created_at)}</p>
                    </div>
                  </div>
                );
              })()}

              {/* Status flags */}
              <div className="flex gap-2 flex-wrap">
                {opportunity?.is_overdue && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                    <AlertTriangle className="w-3 h-3" /> En retard de {opportunity.overdue_hours}h
                  </span>
                )}
                {opportunity?.prospect?.financement_souhaite && !opportunity?.financing && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                    <Banknote className="w-3 h-3" /> Financement à préparer
                  </span>
                )}
                {opportunity?.prospect?.demo_souhaitee && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    <Eye className="w-3 h-3" /> Démo à planifier
                  </span>
                )}
              </div>

              {/* Quick action */}
              <button
                onClick={() => setActivityDialogOpen(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-medium transition-colors"
                data-testid="quick-add-activity"
              >
                <Plus className="w-4 h-4" />
                Ajouter une interaction
              </button>

              {/* Notes with AI task suggestion */}
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
                    <MessageSquare className="w-3.5 h-3.5" />
                    Notes sur le prospect
                  </label>
                  {analyzingNote && (
                    <span className="text-xs text-violet-500 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Analyse...
                    </span>
                  )}
                </div>
                <Textarea
                  value={prospectForm.maturity_notes || opportunity?.prospect?.maturity_notes || ''}
                  onChange={(e) => {
                    setProspectForm({...prospectForm, maturity_notes: e.target.value});
                    // Analyze note after 1 second of no typing
                    clearTimeout(window._noteAnalyzeTimeout);
                    window._noteAnalyzeTimeout = setTimeout(() => {
                      analyzeProspectNote(e.target.value);
                    }, 1000);
                  }}
                  onBlur={(e) => {
                    if (e.target.value !== (opportunity?.prospect?.maturity_notes || '')) {
                      saveProspect({ maturity_notes: e.target.value });
                    }
                  }}
                  placeholder="Ex: Rappeler la semaine prochaine, intéressé par un financement..."
                  rows={3}
                  className="text-sm"
                  data-testid="prospect-maturity-notes"
                />
                
                {/* AI Task Suggestion */}
                {suggestedTask && (
                  <div className="mt-3 p-3 bg-violet-50 border border-violet-100 rounded-xl">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-xs font-medium text-violet-700 flex items-center gap-1.5">
                          <Activity className="w-3.5 h-3.5" />
                          Tâche suggérée
                        </p>
                        <p className="text-sm font-medium text-slate-700 mt-1">{suggestedTask.title}</p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {suggestedTask.suggested_date && `Prévu: ${new Date(suggestedTask.suggested_date).toLocaleDateString('fr-FR')}`}
                          {suggestedTask.priority === 'haute' && (
                            <span className="ml-2 text-red-500 font-medium">• Priorité haute</span>
                          )}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => createTaskFromNote(prospectForm.maturity_notes || opportunity?.prospect?.maturity_notes)}
                        disabled={creatingTask}
                        className="bg-violet-600 hover:bg-violet-700 text-xs"
                        data-testid="create-task-from-note"
                      >
                        {creatingTask ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <>
                            <Plus className="w-3 h-3 mr-1" />
                            Créer
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </TabsContent>

      </Tabs>

      {/* Activity Dialog */}
      <Dialog open={activityDialogOpen} onOpenChange={setActivityDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une activité</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Type de contact</Label>
              <Select 
                value={activityForm.contact_type} 
                onValueChange={(v) => setActivityForm({ ...activityForm, contact_type: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="telephone">Téléphone</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="visite_client">Visite client</SelectItem>
                  <SelectItem value="visite_showroom">Visite showroom</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={activityForm.notes}
                onChange={(e) => setActivityForm({ ...activityForm, notes: e.target.value })}
                placeholder="Détails de l'échange..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActivityDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={async () => {
                try {
                  setSaving(true);
                  await api.post(`/opportunities/${id}/activity`, {
                    contact_type: activityForm.contact_type,
                    notes: activityForm.notes,
                  });
                  toast.success('Activité ajoutée');
                  setActivityDialogOpen(false);
                  setActivityForm({ contact_type: '', contact_result: '', notes: '', next_action_date: '', new_stage: '' });
                  fetchOpportunity();
                } catch (error) {
                  toast.error('Erreur lors de l\'ajout');
                } finally {
                  setSaving(false);
                }
              }}
              disabled={saving || !activityForm.notes}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpportunityDetailPage;
